import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Button from "../../../components/buttons/Button";
import Breadcrumbs from "../../../components/BreadCrumbs";
import StyledHeadingText from "../../../components/Fonts/StyledHeadingText";
import { getHudPatientsPackages, patchHudPatientPackageApproval } from "../../../services/hud/hud_patients";
import { toast } from "react-toastify";
import CustomLoading from "../../../components/customLoading/CustomLoading";
import Pagination from "../../../components/tables/pagination/Pagination";
import Table from "../../../components/tables/Table";
import { formatDateTime } from "../../../utils/FormatDateTime";
import ShowModal from "../../../components/Hud/ShowModal";
import PatientPackageComment from "./patient_package_comment";
import AddPackage from "./add_update_package";
import { BsEye, BsEyeFill } from "react-icons/bs";
import { TbCloudUpload, TbPdf, TbUpload } from "react-icons/tb";
import { CgAdd } from "react-icons/cg";
import { BiCheckCircle, BiEdit } from "react-icons/bi";
import { CiEdit } from "react-icons/ci";
import { pricesColorCoding } from "../../../constants/prices_colors_coding";

const PatientPackages = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const [rowData, setRowData] = useState({});
    const headers = [
        { label: "Action", key: "action" },
        { label: "Package Name", key: "package_name" },
        { label: "Dose/Count", key: "number_of_doses" },
        { label: "Addon Date", key: "created_at" },
        // { label: "Last Comment", key: "last_comment" },
        { label: "Type", key: "package_status" },
        { label: "Color", key: "color" },
        { label: "Comments", key: "package_comments" },
        { label: "Invoice", key: "invoice" },
        // { label: "Edit", key: "edit" },
        { label: "Approval", key: "approval" },
    ];
    const [tableData, setTableData] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(false);

    const pageSize = 10;

    const fetchData = async (patientId) => {
        setLoading(true);
        try {
            let res = await getHudPatientsPackages(patientId);
            if (res.status === 200) {
                setTableData(res.data);
            } else {
                toast.error("Unable to get packages");
            }
            setLoading(false);
        } catch (error) {
            console.error("Error fetching Photos:", error);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    useEffect(() => {
        const patientData = location.state;
        if (patientData) {
            setRowData(patientData);
            fetchData(patientData.patient_id);
        } else {
            toast.error("Error while looking for patient");
            navigate("/hud-patients");
        }
    }, [location.state]);

    const [modalIsOpen, setIsOpen] = useState(false);
    const [modalIsOpenPackage, setIsOpenPackage] = useState(false);
    const [modalIsOpenPackageInoices, setIsOpenPackageInvoices] = useState(false);
    const [openingPackage, setOpeningPackage] = useState({});

    // Close modal
    const closeModal = () => {
        setIsOpen(false);
        setOpeningPackage({});
    };

    const closeAddPackageModal = () => {
        setIsOpenPackage(false);
        setOpeningPackage({});
    };

    const closeAddPackageInvoiceModal = () => {
        setIsOpenPackageInvoices(false);
        setOpeningPackage({});
    };

    const handleCommentAdded = () => {
        closeModal();
        fetchData(rowData.patient_id);
    };

    const handlePackageAdded = () => {
        closeAddPackageModal();
        fetchData(rowData.patient_id);
    };

    // Open modal with the selected PDF ID
    const openModal = (item) => {
        setOpeningPackage(item);
        setIsOpen(true);
    };

    const openModalAddPackage = (item) => {
        setIsOpenPackage(true);
        setOpeningPackage(item || {});
    };

    const openModalAddPackageInvoices = (item) => {
        setOpeningPackage(item);
        setIsOpenPackageInvoices(true);
    };

    const handleUploadApproval = async (packageId, img) => {
        try {
            const formData = new FormData();
            if (img && typeof img === "object") {
                formData.append("approval_image", img);
            }
            let res = await patchHudPatientPackageApproval(packageId, formData);
            if (res.status === 200) {
                toast.success("Approval updated successfully");
                fetchData(rowData.patient_id);
            } else {
                toast.error("Error Updating Approval");
            }
            setLoading(false);
        } catch (error) {
            console.error("Error Updating:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddImage = async (packageId) => {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = "image/*";
        input.onchange = (e) => {
            const file = e.target.files[0];
            handleUploadApproval(packageId, file);
        };
        input.click();
    };

    return (
        loading ? <CustomLoading /> :
            <div className="p-6 bg-white rounded-lg shadow-md">
                <div className="flex flex-row justify-between items-center">
                    <div className="flex flex-row gap-2">
                        <StyledHeadingText size={"text-xs"} text={"Patient: "} />
                        <StyledHeadingText size={"text-xs"} TextColor="text-red-600" text={`${rowData.full_name} (${"Age: " + rowData.age || "DOB: " + rowData.date_of_birth} | Gen: ${rowData.gender})`} />
                    </div>
                    <Breadcrumbs items={[
                        { name: 'Home', link: '/invoice' },
                        { name: 'Patients', link: '/hud-patients' },
                        { name: 'Patient Packages' },
                    ]} />
                </div>
                <div className="w-full flex-1">
                    <div className="flex flex-row justify-between items-center my-4">
                        <Button
                            type="button"
                            className="bg-primary text-white px-4 py-2 rounded text-xs"
                            onClick={() => {
                                if (tableData?.results.length === 0 || tableData?.results !== null && tableData?.results[0].remaining_amount <= 0) {
                                    openModalAddPackage(null)
                                } else {
                                    toast.warning(`Please collect remaining amount of ₹${tableData?.results[0].remaining_amount} for last package`);
                                }
                            }}
                            disabled={loading}
                        >
                            {loading ? "Adding..." : "Add Package"}
                        </Button>
                    </div>
                    {loading ?
                        <div className="flex w-full flex-row justify-center my-2">
                            <CustomLoading size={100} />
                        </div>
                        :
                        <Table
                            headers={headers}
                            rows={tableData?.results?.map((item, index) => (
                                {
                                    created_at: <p>{formatDateTime(item.date)}</p>,
                                    package_name: <p>{item.package_name}</p>,
                                    number_of_doses: <p>{item.number_of_doses}/18</p>,
                                    color: <div className="flex flex-row items-center gap-2 font-bold"><p>{pricesColorCoding.find((e) => e.amount === item.amount)?.color ?? `₹${item.amount}`}</p> <span className={`w-3 h-3 rounded-full border shadow ${pricesColorCoding.find((e) => e.amount === item.amount)?.color ? "" : "hidden"} ${pricesColorCoding.find((e) => e.amount === item.amount)?.bg ?? ""}`}></span></div>,
                                    last_comment: <p className="max-w-20 truncate" title={item.last_comment}>{item.last_comment}</p>,
                                    action: <Link to={"/hud-patients/package/doses/"} state={{ package: item, patient: rowData }}><Button className="bg-primary rounded-sm text-white text-xs">Doses</Button></Link>,
                                    approval: <Button className={`${item.amount === 10000 && item.package_name.toUpperCase() === "FULL BODY" ? "hidden" : ""} ${item.approval_image ? "bg-green-500" : "bg-primary"} rounded-sm text-white text-xs`} onClick={item.approval_image ? () => { window.open(item.approval_image, "_blank"); } : () => handleAddImage(item.package_id)}>{item.approval_image ? <BiCheckCircle color="white" size={14} /> : <TbCloudUpload color="white" size={14} />}</Button>,
                                    package_status: <p>{item.package_status}</p>,
                                    // package_status: <div className='min-w-16'>
                                    //     <Dropdown
                                    //         placeholder={item.package_status}
                                    //         options={[
                                    //             item.package_status !== "Paid" ? { label: "Paid", value: "Paid" } : { label: "FOC", value: "FOC" },
                                    //         ]}
                                    //         onChange={(value) => handleStatusChange(item, value)}
                                    //         className="text-xs"
                                    //     />
                                    // </div>,
                                    package_comments: <div className="flex">
                                        <input
                                            value={item.last_comment}
                                            placeholder={"Comment here..."}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-sm text-red-600 font-bold focus:outline-none focus:border-blue-500"
                                        />
                                        <Button onClick={() => openModal(item)} className="bg-primary rounded-sm text-white text-xs">Show</Button></div>,
                                    invoice: <div className="flex flex-row gap-2 items-center">{<BsEyeFill size={18} color="green" className="cursor-pointer" onClick={() => openModalAddPackageInvoices(item)} />} {item.remaining_amount > 0 ? <Link to={"/hud-patients/packages/add-invoice"} state={item}><CgAdd size={18} color="primary" /></Link> : <></>}</div>,
                                    edit: item.amount === 0 ? <CiEdit size={18} className="text-primary cursor-pointer"
                                        onClick={() => openModalAddPackage(item)}
                                    /> : null,
                                }))}
                        />
                    }
                    {tableData.count !== 0 ?
                        <div className="xl:pl-5 pl-0 w-full flex-1">
                            <Pagination
                                currentPage={currentPage}
                                totalPages={Math.ceil(tableData?.count / pageSize)}
                                pageSize={pageSize}
                                totalEntries={tableData?.count}
                                onPageChange={handlePageChange}
                                data={tableData}
                                setTableData={setTableData}
                            />
                        </div> : <></>}
                </div>

                <Button
                    type="button"
                    className=" text-xs bg-gray-500 text-white px-4 py-2 rounded mt-2"
                    onClick={() => navigate(-1)}
                >
                    Back
                </Button>
                <ShowModal contentLabel={"Comment alert"} isOpen={modalIsOpen} onRequestClose={closeModal} componentToPass={<PatientPackageComment packageId={openingPackage.package_id} onAddNewComment={handleCommentAdded} packageComments={openingPackage.package_comments} />} />
                <ShowModal contentLabel={"Add Package alert"} modalWidth={"20%"} isOpen={modalIsOpenPackage} onRequestClose={closeAddPackageModal} componentToPass={<AddPackage patientId={rowData.patient_id} onAddNewPackage={handlePackageAdded} initialValues={openingPackage} />} />
                <ShowModal
                    contentLabel={"Package Invoices"}
                    isOpen={modalIsOpenPackageInoices}
                    onRequestClose={closeAddPackageInvoiceModal}
                    componentToPass={
                        <div className="flex flex-col space-y-4 mt-4">
                            <div className="flex flex-row items-center gap-2 mb-2">
                                <p className="font-bold text-xs">Pkg. Color: {pricesColorCoding.find((e) => e.amount === openingPackage.amount)?.color ?? `₹${openingPackage.amount}`}</p> <span className={`w-6 h-3 rounded-full border shadow ${pricesColorCoding.find((e) => e.amount === openingPackage.amount)?.color ? "" : "hidden"} ${pricesColorCoding.find((e) => e.amount === openingPackage.amount)?.bg ?? ""}`}></span> <p className="font-bold text-xs">{pricesColorCoding.find((e) => e.amount === openingPackage.amount)?.short_hand ?? ``}</p>
                                <p className="font-bold text-xs"> | Rem. Color: {pricesColorCoding.find((e) => e.amount === openingPackage.remaining_amount)?.color ?? `₹${openingPackage.remaining_amount}`}</p> <span className={`w-6 h-3 rounded-full border shadow ${pricesColorCoding.find((e) => e.amount === openingPackage.remaining_amount)?.color ? "" : "hidden"} ${pricesColorCoding.find((e) => e.amount === openingPackage.remaining_amount)?.bg ?? ""}`}></span> <p className="font-bold text-xs">{pricesColorCoding.find((e) => e.amount === openingPackage.remaining_amount)?.short_hand ?? ``}</p>
                            </div>
                            {/* Render each invoice in a row */}
                            {(openingPackage.invoices || []).map((item, index) => (
                                <div
                                    key={index}
                                    className="flex justify-between items-center cursor-pointer bg-gray-100 p-4 rounded-md shadow-sm hover:shadow-md transition-shadow"
                                    onClick={() => window.open(item.url, "_blank", "noopener,noreferrer")}
                                >
                                    {/* Date on the left */}
                                    <span className="text-gray-800 text-sm font-medium">
                                        <div className="flex flex-row items-center gap-2 mb-2">
                                            <p className="font-bold text-xs">Color: {pricesColorCoding.find((e) => e.amount === item.amount)?.color ?? `₹${item.amount}`}</p> <span className={`w-6 h-3 rounded-full border shadow ${pricesColorCoding.find((e) => e.amount === item.amount)?.color ? "" : "hidden"} ${pricesColorCoding.find((e) => e.amount === item.amount)?.bg ?? ""}`}></span> <p className="font-bold text-xs">{pricesColorCoding.find((e) => e.amount === item.amount)?.short_hand ?? ``}</p>
                                        </div>
                                    </span>

                                    {/* View Icon on the right */}
                                    <button
                                        className="text-primary hover:text-blue-800 transition-colors"
                                    >
                                        <TbPdf />
                                    </button>
                                </div>
                            ))}
                        </div>
                    }
                />
            </div >
    );
};

export default PatientPackages;
